import { useRef, useState, type CSSProperties } from 'react';
import classNames from 'classnames';
import { ModalBody } from '@carbon/react';
import type { AreaGeometry, GeometryUpload } from 'types/area-geometry';
import type { AreaWorkspace } from 'types/area-workspace';
import { EditMethodRail } from './edit-method-rail';
import { EditorMap } from './editor-map';
import { EditorSplitter } from './editor-splitter';
import { EditorToolbar } from './editor-toolbar';
import {
  useEditorSession,
  useHighlightGeometries,
  useToolsWidth,
} from './hooks';
import type { SavedDraftGeometry } from './types';
import { EDITED_AREA_COLOR } from './utils';
import styles from './area-geometry-editor.module.scss';

interface EditorBodyProps {
  area: AreaGeometry;
  workspace: AreaWorkspace;
  upload: GeometryUpload;
  onClose: () => void;
  onSaved: (saved: SavedDraftGeometry) => void;
}

/**
 * The editor's body for one session: the map with its tools, the splitter, and
 * the tools column with Cancel and Save docked under it.
 *
 * @param {EditorBodyProps} props - The area, its workspace and the exits.
 * @return {JSX.Element} The body.
 */
export function EditorBody({
  area,
  workspace,
  upload,
  onClose,
  onSaved,
}: EditorBodyProps): JSX.Element {
  const session = useEditorSession(area, workspace, upload);
  const { parts, dirty, draft, edit, method, tool, toolsEnabled, pendingEdit } =
    session;
  const highlightGeometries = useHighlightGeometries(session.highlightedIds);
  const columnsRef = useRef<HTMLDivElement>(null);
  const toolsWidth = useToolsWidth(columnsRef);
  const [resizing, setResizing] = useState(false);

  /** Saves the working copy as the draft, and closes once it lands. */
  const handleSave = async () => {
    if (!pendingEdit) return;
    const saved = await draft.save(pendingEdit);
    if (saved) {
      onSaved(saved);
      onClose();
    }
  };

  return (
    <ModalBody className={styles.modalBody}>
      <div
        ref={columnsRef}
        className={classNames(styles.editorColumns, {
          [styles.editorColumnsResizing]: resizing,
        })}
        style={{ '--tools-width': `${toolsWidth.width}px` } as CSSProperties}
      >
        <div className={styles.mapWorkspace}>
          <EditorToolbar session={session} />
          <div
            className={styles.mapArea}
            id="area-geometry-edit-map"
            tabIndex={-1}
          >
            <EditorMap
              parts={parts}
              holesFrom={session.holesFrom}
              areaName={area.displayName || area.name}
              drawn={edit.pieces}
              color={EDITED_AREA_COLOR}
              tool={tool}
              toolsEnabled={toolsEnabled}
              onDrawnChange={edit.setPieces}
              onShapeCommit={session.commitShapes}
              onBeforeChange={edit.pushHistory}
              onSurveyAreaPick={
                method === 'survey-description' ? session.surveyPick.pick : null
              }
              pickedSurveyIds={session.pickedSurveyIds}
              referenceAreas={session.referenceAreas}
              traceBoundaries={session.traceBoundaries}
              landGrid={session.landGridOn}
              snap={session.snap}
              highlightGeometries={highlightGeometries}
              onViewChange={session.setViewBounds}
            />
            {session.landGridZoomIn && (
              <p className={styles.mapNotice} role="status">
                Zoom in to trace the land grid
              </p>
            )}
          </div>
        </div>
        <EditorSplitter
          width={toolsWidth.width}
          onWidthChange={toolsWidth.setWidth}
          onReset={toolsWidth.reset}
          onResizingChange={setResizing}
        />
        <EditMethodRail
          area={area}
          session={session}
          canSave={dirty && pendingEdit !== null}
          onCancel={onClose}
          onSave={() => void handleSave()}
        />
      </div>
    </ModalBody>
  );
}
